import React, { useMemo } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import SoaTableRenderer from '../../protocol/sections/s9-assessment-schedule/components/SoaTableRenderer';
import { legacySoaPayloadToS9Table } from '../../utils/soaTableExportModel';
import ScheduleOfActivities from '../tables/ScheduleOfActivities';

/**
 * Shared Schedule of Activities display for Document Editor edit + canvas modes.
 * Prefers chat-based S9 tables (SoaTableRenderer); falls back to legacy shape.
 */
const PublishedSoaView = ({
  s9SoaTables = [],
  s9PublishedVersion = null,
  generatedSoAData = null,
  protocolId = null,
  showHeader = true,
  emptyMessage = 'No SOA published yet. Open the SOA tab, build the schedule with chat, then Publish to protocol.',
}) => {
  // Prefer real S9-published tables; otherwise, a USDM-generated section can
  // carry the same rich {tableId, columns, rowGroups, grid, footnotes} shape
  // under generatedSoAData.tables (see usdmCatalogueContext.js's `soaGrid`
  // extraction) — render those through the same SoaTableRenderer so USDM-
  // sourced content gets the identical grouped/tiered-header table instead
  // of the flatter ScheduleOfActivities fallback below.
  const hasS9Tables = Array.isArray(s9SoaTables) && s9SoaTables.length > 0;
  const embeddedTables = Array.isArray(generatedSoAData?.tables) && generatedSoAData.tables.length
    ? generatedSoAData.tables
    : Array.isArray(generatedSoAData?.s9?.tables) && generatedSoAData.s9.tables.length
      ? generatedSoAData.s9.tables
      : null;
  const hasUsdmTables = !hasS9Tables && Boolean(embeddedTables);
  // Flat {visits, sections} data (e.g. rebuilt from a USDM ScheduleTimeline with no
  // extension:soaGrid) is converted so it still renders with the S9 table look.
  const convertedTable = useMemo(
    () => (!hasS9Tables && !embeddedTables ? legacySoaPayloadToS9Table(generatedSoAData) : null),
    [hasS9Tables, embeddedTables, generatedSoAData],
  );
  const tables = hasS9Tables
    ? s9SoaTables
    : embeddedTables || (convertedTable ? [convertedTable] : []);

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {showHeader && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          1.3 Schedule of Activities
          {s9PublishedVersion != null ? ` — published v${s9PublishedVersion}` : ''}
          {hasS9Tables && !s9PublishedVersion ? ' — from SOA workspace' : ''}
          {(hasUsdmTables || convertedTable) && !generatedSoAData?.s9 ? ' — from uploaded USDM catalogue' : ''}
        </Typography>
      )}

      {tables.length > 0 ? (
        <Stack spacing={3} sx={{ width: '100%', minWidth: 0 }}>
          {tables.map((table) => (
            <Box
              key={table.tableId || table.label}
              sx={{
                width: '100%',
                minWidth: 0,
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                p: { xs: 1, md: 1.5 },
              }}
            >
              <SoaTableRenderer
                table={table}
                allTables={tables}
                variant="protocol"
              />
            </Box>
          ))}
        </Stack>
      ) : generatedSoAData?.visits || generatedSoAData?.sections ? (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <ScheduleOfActivities
            data={generatedSoAData}
            protocolId={protocolId}
            readOnly
          />
        </Box>
      ) : (
        <Alert severity="info">{emptyMessage}</Alert>
      )}
    </Box>
  );
};

export default PublishedSoaView;
